import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Settings, Phone, Mail, MessageSquare, Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    support_whatsapp: '',
    support_email: '',
    support_phone: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        support_whatsapp: response.data.support_whatsapp || '+916382947171',
        support_email: response.data.support_email || 'info@livicot.com',
        support_phone: response.data.support_phone || '+916382947171'
      });
    } catch (err) {
      console.error('Error fetching settings:', err);
      // Use defaults if fetch fails
      setSettings({
        support_whatsapp: '+916382947171',
        support_email: 'info@livicot.com',
        support_phone: '+916382947171'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    // Validate WhatsApp number
    if (!settings.support_whatsapp || settings.support_whatsapp.length < 10) {
      setError('Please enter a valid WhatsApp number');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
    // Format: +91 82480 75883
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 12 && cleaned.startsWith('91')) {
      return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    return phone;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Platform Settings
          </h1>
          <p className="text-slate-500 mt-1">Manage centralized settings for the platform</p>
        </div>
        <button
          onClick={fetchSettings}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Support Contact Settings Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
      >
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-violet-100 rounded-lg">
              <MessageSquare className="text-violet-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Support Contact Information</h2>
              <p className="text-sm text-slate-500">These contact details will be used across the entire platform</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Alerts */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="text-red-500" size={20} />
              <p className="text-red-700">{error}</p>
            </div>
          )}
          {success && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="text-emerald-500" size={20} />
              <p className="text-emerald-700">{success}</p>
            </div>
          )}

          {/* WhatsApp Number */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <MessageSquare size={16} className="text-green-500" />
              Support WhatsApp Number
            </label>
            <input
              type="text"
              name="support_whatsapp"
              value={settings.support_whatsapp}
              onChange={handleChange}
              placeholder="+91 82480 75883"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-lg font-mono"
            />
            <p className="mt-2 text-sm text-slate-500">
              Format: +91XXXXXXXXXX (with country code). This number will appear on login page, subscription screens, and contact support links.
            </p>
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                <strong>Preview:</strong> {formatPhoneForDisplay(settings.support_whatsapp)}
              </p>
            </div>
          </div>

          {/* Support Email */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Mail size={16} className="text-blue-500" />
              Support Email
            </label>
            <input
              type="email"
              name="support_email"
              value={settings.support_email}
              onChange={handleChange}
              placeholder="support@gpos.in"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <p className="mt-2 text-sm text-slate-500">
              Email address for support inquiries.
            </p>
          </div>

          {/* Support Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Phone size={16} className="text-violet-500" />
              Support Phone Number
            </label>
            <input
              type="text"
              name="support_phone"
              value={settings.support_phone}
              onChange={handleChange}
              placeholder="+91 82480 75883"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
            />
            <p className="mt-2 text-sm text-slate-500">
              Phone number for voice support (can be same as WhatsApp).
            </p>
          </div>

          {/* Usage Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Where these settings are used:</h4>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>POS Login Page - "Need Help? Contact Support" button</li>
              <li>Subscription Settings - "Need Help?" WhatsApp link</li>
              <li>Subscription Gate - Contact support for deactivated accounts</li>
              <li>Payment pending screens - WhatsApp support link</li>
              <li>Landing Page - Contact information</li>
            </ul>
          </div>
        </div>

        {/* Save Button */}
        <div className="p-6 border-t border-slate-200 bg-slate-50">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save Settings
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Quick Test Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-6"
      >
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Quick Test</h3>
        <p className="text-sm text-slate-500 mb-4">Test the support contact links:</p>
        <div className="flex flex-wrap gap-3">
          <a
            href={`https://wa.me/${settings.support_whatsapp.replace(/[^0-9]/g, '')}?text=Test message from GPOS Admin`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <MessageSquare size={18} />
            Test WhatsApp
          </a>
          <a
            href={`mailto:${settings.support_email}?subject=Test from GPOS Admin`}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Mail size={18} />
            Test Email
          </a>
          <a
            href={`tel:${settings.support_phone}`}
            className="flex items-center gap-2 px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-colors"
          >
            <Phone size={18} />
            Test Phone
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminSettings;
